import { unwrap } from '@qrkey/api-client';
import { TERMINAL_ROLES, type TerminalRole } from '@qrkey/contracts';
import { Alert, Button, Card, Field, TextField, useToast } from '@qrkey/ui';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { api, useSession } from '../app/api.js';
import { isTillRole, newDeviceSecret, terminalStore, useTerminal } from '../app/terminal.js';
import { errorMessage } from '../lib/errors.js';
import { shortId } from '../lib/permissions.js';
import { signOut } from '../lib/sign-out.js';
import { useMe } from './Shell.js';

const ROLE_TEXT: Record<TerminalRole, [string, string]> = {
  pos: ['Till (POS)', 'Billing counter: orders, KOT, bills and payments'],
  captain: ['Captain', 'Phone for taking orders at tables'],
  kds: ['Kitchen display', 'Tickets for a kitchen station'],
  hub: ['Outlet hub', 'Keeps the outlet running on the LAN when offline'],
  kiosk: ['Kiosk', 'Self-ordering screen'],
  backoffice: ['Back office', 'Office computer for setup and reports'],
};

/**
 * Registers this device as a terminal of an outlet (08-clients.md "Staff app"): a
 * manager signed in to the back office picks the outlet and role; the device makes a
 * random secret and keeps it, the server keeps only its hash.
 */
export function RegisterTerminalScreen() {
  const session = useSession();
  const me = useMe();
  const navigate = useNavigate();
  const toast = useToast();
  const existing = useTerminal();
  const outlets = (me.data?.outlets ?? []).filter((o) =>
    o.permissions.includes('terminals.register'),
  );
  const [outletId, setOutletId] = useState<string>();
  const [name, setName] = useState('Counter 1');
  const [role, setRole] = useState<TerminalRole>('pos');
  const [nameError, setNameError] = useState<string>();
  const chosenOutlet = outletId ?? outlets[0]?.outletId;

  const register = useMutation({
    mutationFn: async () => {
      if (!chosenOutlet || !session) throw new Error('No outlet');
      const secret = newDeviceSecret();
      const terminal = await unwrap(
        api.client.POST('/v1/outlets/{outletId}/terminals/register', {
          params: { path: { outletId: chosenOutlet } },
          body: {
            name: name.trim(),
            role,
            platform: 'web',
            appVersion: __QRKEY_APP_VERSION__,
            deviceSecret: secret,
          },
        }),
      );
      return { terminal, secret };
    },
    onSuccess: async ({ terminal, secret }) => {
      if (!session) return;
      terminalStore.set({
        companyId: session.session.companyId,
        outletId: terminal.outletId,
        terminalId: terminal.id,
        secret,
        role: terminal.role,
        name: terminal.name,
      });
      toast.show({ tone: 'success', title: `${terminal.name} is set up` });
      if (isTillRole(terminal.role)) {
        // A till is used by PIN: the manager's back-office session should not stay on it.
        await signOut();
        void navigate({ to: '/pin' });
      } else {
        void navigate({ to: '/' });
      }
    },
  });

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length === 0) {
      setNameError('Give the device a name staff will recognise');
      return;
    }
    setNameError(undefined);
    register.mutate();
  }

  if (session?.session.surface !== 'backoffice') {
    return (
      <Card title="Set up this device" titleLevel={1}>
        <Alert tone="info">A manager signed in to the back office sets up devices.</Alert>
      </Card>
    );
  }

  return (
    <Card
      title="Set up this device"
      titleLevel={1}
      description="Register this device as a terminal of an outlet. Do this once per device."
    >
      <form className="stack" onSubmit={onSubmit} noValidate>
        {existing ? (
          <Alert tone="warning">
            This device is already {existing.name}. Setting it up again replaces that registration
            on this device.
          </Alert>
        ) : null}
        {me.data && outlets.length === 0 ? (
          <Alert tone="warning">You cannot register terminals at any outlet.</Alert>
        ) : null}
        {register.isError ? <Alert tone="danger">{errorMessage(register.error)}</Alert> : null}
        {outlets.length > 1 ? (
          <Field label="Outlet">
            {(control) => (
              <select
                {...control}
                className="qk-input"
                value={chosenOutlet}
                onChange={(e) => setOutletId(e.target.value)}
              >
                {outlets.map((o) => (
                  <option key={o.outletId} value={o.outletId}>
                    Outlet {shortId(o.outletId)}
                  </option>
                ))}
              </select>
            )}
          </Field>
        ) : null}
        <TextField
          label="Device name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={nameError}
          maxLength={60}
        />
        <fieldset className="qk-fieldset">
          <legend className="qk-fieldset__legend">What is this device for?</legend>
          <div className="choice-grid">
            {TERMINAL_ROLES.map((r) => (
              <label key={r} className="choice">
                <input
                  type="radio"
                  name="role"
                  value={r}
                  checked={role === r}
                  onChange={() => setRole(r)}
                />
                <span className="choice__text">
                  <span className="choice__name">{ROLE_TEXT[r][0]}</span>
                  <span className="choice__hint">{ROLE_TEXT[r][1]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
        <Button type="submit" size="lg" loading={register.isPending} disabled={!chosenOutlet}>
          Register device
        </Button>
      </form>
    </Card>
  );
}
